import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  type ChatModelAdapter,
  type ChatModelRunResult,
  WebSpeechDictationAdapter,
} from "@assistant-ui/react";
import type { ReadonlyJSONObject, ReadonlyJSONValue } from "assistant-stream/utils";
import { sendChatMessage, streamChatMessage, type PageContextPayload } from "@/lib/api";
import { clearSnapshot, loadSnapshot, saveSnapshot } from "@/lib/conversation-storage";
import {
  ConversationControlContext,
  type ConversationControl,
} from "@/runtime/conversation-control";
import { detectBrowserLocale, t, toBcp47 } from "@/i18n";
import { useLocale } from "@/locale-context";
import { useSidePanelStore } from "@/stores/side-panel-store";

export type PageContextLike =
  | PageContextPayload
  | { pageType: string; makeId?: string | null; modelId?: string | null };

type ApiRuntimeProviderProps = {
  children: ReactNode;
  /**
   * Host-page context. The chat widget pre-scopes vehicle searches based on
   * this. Sources (in priority order): explicit prop → window.__WILDSTREAM_PAGE_CONTEXT__
   * → URL params (?page=vdp&make=...&model=...). Pass null to opt out.
   */
  pageContext?: PageContextLike | null;
};

type ToolCallPart = {
  type: "tool-call";
  toolCallId: string;
  toolName: string;
  args: ReadonlyJSONObject;
  argsText: string;
  result?: ReadonlyJSONValue;
  status: { type: "running" } | { type: "complete" };
};

/**
 * Bridges @assistant-ui/react to the booking assistant's SSE streaming endpoint.
 * Falls back to the blocking POST /api/chat if streaming fails.
 */
export function ApiRuntimeProvider({ children, pageContext }: ApiRuntimeProviderProps) {
  const conversationIdRef = useRef<string | null>(null);
  const pageContextRef = useRef<PageContextLike | null | undefined>(pageContext);
  const locale = useLocale();
  pageContextRef.current = pageContext;

  // Read persisted conversation once, synchronously, before the runtime is
  // created and before children render. Seeding the id ref + window global
  // here (rather than in useEffect) ensures that components like
  // vehicle-stock-card and dealer-location-tool-card — which read
  // window.__WILDSTREAM_CONVERSATION_ID__ during render — see the right id on
  // their very first paint when restoring a session.
  const initialSnapshot = useMemo(() => {
    const snapshot = loadSnapshot();
    if (!snapshot) return null;
    conversationIdRef.current = snapshot.conversationId;
    if (typeof window !== "undefined") {
      (
        window as Window & { __WILDSTREAM_CONVERSATION_ID__?: string }
      ).__WILDSTREAM_CONVERSATION_ID__ = snapshot.conversationId;
      if (snapshot.conversationLanguage) {
        window.__WILDSTREAM_LOCALE_UPDATE__?.(snapshot.conversationLanguage);
      }
    }
    console.info("[ApiRuntime] restored conversation from storage", {
      conversationId: snapshot.conversationId,
      language: snapshot.conversationLanguage,
      messageCount: snapshot.export.messages?.length ?? 0,
    });
    return snapshot;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sttNative = "SpeechRecognition" in window || "webkitSpeechRecognition" in window;
    const ttsNative = "speechSynthesis" in window;
    console.info(`[Voice] native STT supported: ${sttNative} | native TTS supported: ${ttsNative}`);
  }, []);

  const adapter = useMemo<ChatModelAdapter>(
    () => ({
      async *run({ messages, abortSignal }) {
        const last = messages[messages.length - 1];
        const userText =
          last?.role === "user"
            ? last.content
                .filter((part) => part.type === "text")
                .map((part) => part.text)
                .join(" ")
            : "";

        const resolvedContext = resolvePageContext(pageContextRef.current);
        const request = {
          message: userText,
          conversationId: conversationIdRef.current,
          ...(resolvedContext ? { pageContext: resolvedContext } : {}),
        };

        let streamed = false;
        let yieldedContent = false;
        try {
          for await (const result of streamResponse(request, conversationIdRef, abortSignal)) {
            yieldedContent = true;
            yield result;
          }
          streamed = true;
        } catch (err) {
          if (abortSignal.aborted) return;
          // Streaming failed — fall back to non-streaming endpoint
          console.warn("SSE stream failed, falling back to blocking call:", err);
        }

        if (!streamed) {
          if (yieldedContent) {
            // Partial content was already shown — don't replace it with a full retry.
            yield {
              status: {
                type: "incomplete" as const,
                reason: "error" as const,
                error: t(detectBrowserLocale(), "runtime.interrupted"),
              },
            };
          } else {
            const response = await sendChatMessage(request, abortSignal);
            conversationIdRef.current = response.conversationId;
            if (response.conversationLanguage && typeof window !== "undefined") {
              window.__WILDSTREAM_LOCALE_UPDATE__?.(response.conversationLanguage);
            }
            yield { content: [{ type: "text" as const, text: response.message }] };
          }
        }
      },
    }),
    [],
  );

  const dictationAdapter = useMemo(() => {
    if (typeof window === "undefined" || !WebSpeechDictationAdapter.isSupported()) {
      return undefined;
    }

    return new WebSpeechDictationAdapter({
      language: toBcp47(locale),
      // `continuous: false` lets the browser end recognition on a sustained
      // pause (~1-2s), which matches our Azure silence-detector UX. With
      // `continuous: true` the user would need to click the mic a second
      // time to stop, breaking the auto-send flow.
      continuous: false,
      interimResults: true,
    });
  }, [locale]);

  const runtime = useLocalRuntime(adapter, {
    adapters: dictationAdapter ? { dictation: dictationAdapter } : undefined,
  });

  // Hydrate messages from the persisted snapshot before first paint. Using
  // useLayoutEffect keeps the flash of empty thread invisible to the user.
  // thread.import preserves message ids, branches, and tool-call result
  // payloads, which is what the card renderers in ToolPartRenderer consume.
  const hydratedRef = useRef(false);
  useLayoutEffect(() => {
    if (hydratedRef.current) return;
    if (!initialSnapshot) {
      hydratedRef.current = true;
      return;
    }
    try {
      runtime.thread.import(initialSnapshot.export);
      console.info("[ApiRuntime] thread hydrated from snapshot");
    } catch (err) {
      console.error("[ApiRuntime] thread hydrate failed, clearing snapshot", err);
      clearSnapshot();
      conversationIdRef.current = null;
      if (typeof window !== "undefined") {
        delete (window as Window & { __WILDSTREAM_CONVERSATION_ID__?: string })
          .__WILDSTREAM_CONVERSATION_ID__;
      }
    }
    hydratedRef.current = true;
  }, [runtime, initialSnapshot]);

  // Persist the exported thread on every running→idle transition. We only
  // save when a turn finishes (not mid-stream) so tool-call parts always
  // have their final "complete" status and the card renderers don't fall
  // back to ToolFallback on reload.
  const localeRef = useRef(locale);
  localeRef.current = locale;
  useEffect(() => {
    const thread = runtime.thread;
    let prevRunning = thread.getState().isRunning;
    const unsubscribe = thread.subscribe(() => {
      const state = thread.getState();
      const running = state.isRunning;
      if (prevRunning && !running) {
        const id = conversationIdRef.current;
        if (id && state.messages.length > 0) {
          try {
            saveSnapshot({
              conversationId: id,
              conversationLanguage: localeRef.current ?? null,
              export: thread.export(),
              savedAt: Date.now(),
            });
          } catch (err) {
            console.warn("[ApiRuntime] saveSnapshot threw", err);
          }
        }
      }
      prevRunning = running;
    });
    return unsubscribe;
  }, [runtime]);

  const startNewConversation = useCallback(() => {
    const thread = runtime.thread;
    try {
      if (thread.getState().isRunning) {
        thread.cancelRun();
      }
    } catch (err) {
      console.warn("[ApiRuntime] cancelRun threw during reset", err);
    }
    thread.reset();
    conversationIdRef.current = null;
    if (typeof window !== "undefined") {
      delete (window as Window & { __WILDSTREAM_CONVERSATION_ID__?: string })
        .__WILDSTREAM_CONVERSATION_ID__;
    }
    clearSnapshot();

    // The vehicle side panel is conversation-scoped: its contents come from a
    // SearchVehicles / GetVehicleDetails tool call in the thread we are about to
    // discard. Resetting here rather than in the button handler means every
    // caller of startNewConversation gets it, not just the header button.
    useSidePanelStore.getState().close();

    console.info("[ApiRuntime] conversation reset by user");
  }, [runtime]);

  const control = useMemo<ConversationControl>(
    () => ({ startNewConversation }),
    [startNewConversation],
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ConversationControlContext.Provider value={control}>
        {children}
      </ConversationControlContext.Provider>
    </AssistantRuntimeProvider>
  );
}

async function* streamResponse(
  request: { message: string; conversationId: string | null },
  conversationIdRef: React.RefObject<string | null>,
  abortSignal: AbortSignal,
): AsyncGenerator<ChatModelRunResult> {
  let accumulatedText = "";
  const toolCalls = new Map<string, ToolCallPart>();

  for await (const { event, data } of streamChatMessage(request, abortSignal)) {
    switch (event) {
      case "metadata": {
        const d = data as { conversationId: string; conversationLanguage?: string };
        conversationIdRef.current = d.conversationId;
        if (typeof window !== "undefined") {
          (
            window as Window & { __WILDSTREAM_CONVERSATION_ID__?: string }
          ).__WILDSTREAM_CONVERSATION_ID__ = d.conversationId;
          if (d.conversationLanguage) {
            window.__WILDSTREAM_LOCALE_UPDATE__?.(d.conversationLanguage);
          }
        }
        break;
      }

      case "text-delta": {
        const d = data as { delta: string };
        accumulatedText += d.delta;
        yield buildResult(accumulatedText, toolCalls);
        break;
      }

      case "tool-call-start": {
        const d = data as {
          toolName: string;
          toolCallId: string;
          arguments?: ReadonlyJSONValue;
        };
        const args = asReadonlyJSONObject(d.arguments);
        toolCalls.set(d.toolCallId, {
          type: "tool-call",
          toolCallId: d.toolCallId,
          toolName: d.toolName,
          args,
          argsText: JSON.stringify(args, null, 2),
          status: { type: "running" },
        });
        yield buildResult(accumulatedText, toolCalls);
        break;
      }

      case "tool-call-end": {
        const d = data as {
          toolName: string;
          toolCallId: string;
          result?: ReadonlyJSONValue;
        };
        // Try exact callId match first, then fall back to matching by toolName
        // (callIds may differ when the SDK generates separate fallback IDs for
        // FunctionCallContent vs FunctionResultContent).
        let matchKey = d.toolCallId;
        if (!toolCalls.has(matchKey)) {
          for (const [key, tc] of toolCalls) {
            if (tc.toolName === d.toolName && tc.status.type === "running") {
              matchKey = key;
              break;
            }
          }
        }
        const existing = toolCalls.get(matchKey);
        if (existing) {
          toolCalls.set(matchKey, {
            ...existing,
            result: d.result ?? null,
            status: { type: "complete" },
          });
        }
        yield buildResult(accumulatedText, toolCalls);
        break;
      }

      case "content-block": {
        const d = data as { blockType: string; payload: unknown };

        if (d.blockType === "slot-picker") {
          const payload = d.payload as Record<string, unknown>;
          let injected = false;
          for (const [key, tc] of toolCalls) {
            if (tc.toolName === "ProposeSlots") {
              toolCalls.set(key, {
                ...tc,
                result: {
                  status: "found",
                  slots: payload.slots,
                  dealer: payload.dealer,
                  acceptanceType: payload.acceptanceType,
                  vehicle: payload.vehicle ?? null,
                  prefill: payload.prefill ?? null,
                } as unknown as ReadonlyJSONValue,
              });
              injected = true;
              break;
            }
          }
          if (!injected) {
            console.warn(
              "[ApiRuntime] slot-picker content-block arrived but no ProposeSlots tool call found in map",
            );
          } else {
            console.log(
              "[ApiRuntime] slot-picker content-block injected into ProposeSlots result",
              {
                slotCount: Array.isArray(payload.slots) ? (payload.slots as unknown[]).length : "?",
              },
            );
          }
          yield buildResult(accumulatedText, toolCalls);
          break;
        }

        if (d.blockType === "booking-confirmation") {
          let injected = false;
          for (const [key, tc] of toolCalls) {
            if (tc.toolName === "ConfirmBooking") {
              toolCalls.set(key, {
                ...tc,
                result: d.payload as ReadonlyJSONValue,
              });
              injected = true;
              break;
            }
          }
          if (!injected) {
            console.warn(
              "[ApiRuntime] booking-confirmation content-block arrived but no ConfirmBooking tool call found in map",
            );
          }
          yield buildResult(accumulatedText, toolCalls);
          break;
        }

        if (d.blockType === "typology-card") {
          let injected = false;
          for (const [key, tc] of toolCalls) {
            if (tc.toolName === "ClassifyCustomerTypology") {
              toolCalls.set(key, {
                ...tc,
                result: d.payload as ReadonlyJSONValue,
              });
              injected = true;
              break;
            }
          }
          if (!injected) {
            console.warn(
              "[ApiRuntime] typology-card content-block arrived but no ClassifyCustomerTypology tool call found in map",
            );
          }
          yield buildResult(accumulatedText, toolCalls);
          break;
        }

        console.log("[ApiRuntime] unknown content-block type, embedding as markdown", {
          blockType: d.blockType,
        });
        const json = JSON.stringify({ __block: d.blockType, data: d.payload });
        accumulatedText += `\n\n\`\`\`${d.blockType}\n${json}\n\`\`\`\n\n`;
        yield buildResult(accumulatedText, toolCalls);
        break;
      }

      case "done":
        yield {
          ...buildResult(accumulatedText, toolCalls),
          status: { type: "complete" as const, reason: "stop" as const },
        };
        return;

      case "error": {
        const d = data as { message: string };
        yield {
          ...buildResult(accumulatedText, toolCalls),
          status: {
            type: "incomplete" as const,
            reason: "error" as const,
            error: d.message,
          },
        };
        return;
      }
    }
  }

  // Stream ended without an explicit done/error event — emit a terminal status
  // so the UI exits the "generating" state.
  yield {
    ...buildResult(accumulatedText, toolCalls),
    status: { type: "complete" as const, reason: "stop" as const },
  };
}

function buildResult(text: string, toolCalls: Map<string, ToolCallPart>): ChatModelRunResult {
  return {
    content: [...(text ? [{ type: "text" as const, text }] : []), ...toolCalls.values()],
  };
}

function asReadonlyJSONObject(value: ReadonlyJSONValue | undefined): ReadonlyJSONObject {
  if (!value || Array.isArray(value) || typeof value !== "object") {
    return {};
  }

  return value as ReadonlyJSONObject;
}

declare global {
  interface Window {
    __WILDSTREAM_PAGE_CONTEXT__?: PageContextLike;
  }
}

/**
 * Resolve the page-context block sent with each chat turn. Priority:
 *   1. Explicit prop passed to ApiRuntimeProvider.
 *   2. window.__WILDSTREAM_PAGE_CONTEXT__ (host page injects at load).
 *   3. URL params ?page=vdp&make=volkswagen&model=volkswagen-id-4.
 * Returns null when nothing is set — the API treats that as "unknown".
 */
function resolvePageContext(
  explicit: PageContextLike | null | undefined,
): PageContextPayload | null {
  if (explicit === null) return null;

  const raw: PageContextLike | undefined =
    explicit ?? (typeof window !== "undefined" ? window.__WILDSTREAM_PAGE_CONTEXT__ : undefined);

  let pageType = raw?.pageType ?? "";
  let makeId = raw?.makeId ?? undefined;
  let modelId = raw?.modelId ?? undefined;

  if (!pageType && typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    pageType = params.get("page") ?? "";
    makeId = makeId ?? params.get("make") ?? undefined;
    modelId = modelId ?? params.get("model") ?? undefined;
  }

  if (!pageType) return null;

  return {
    pageType,
    makeId: makeId ?? undefined,
    modelId: modelId ?? undefined,
  };
}
